import { SimpleGrid, Stack, Paper, Title, Text, Group, Badge, Progress, Alert, Skeleton } from '@mantine/core';
import {
  IconCurrencyEuro,
  IconClick,
  IconEye,
  IconTarget,
  IconChartBar,
  IconAlertCircle,
} from '@tabler/icons-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  useGoogleAdsAccount,
  useGoogleAdsAuditSummary,
  useGoogleAdsPerformance,
} from '@/hooks/useGoogleAds';
import { microsToAmount, formatPercentage } from '@/types/googleAds';
import type { DateRangeOption } from '@/types/googleAds';
import { MetricCard } from '../components/MetricCard';

interface OverviewTabProps {
  customerId: string;
  dateRange: DateRangeOption;
}

export function OverviewTab({ customerId, dateRange }: OverviewTabProps) {
  const { data: account, isLoading: accountLoading } = useGoogleAdsAccount(customerId);
  const { data: summary, isLoading: summaryLoading } = useGoogleAdsAuditSummary(customerId, dateRange);
  const { data: performance, isLoading: performanceLoading } = useGoogleAdsPerformance(customerId, dateRange);

  const isLoading = accountLoading || summaryLoading || performanceLoading;

  const formatCurrency = (micros: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: account?.currency ?? 'EUR' }).format(
      microsToAmount(micros),
    );

  const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(num);

  return (
    <Stack gap="lg">
      {/* Account Info */}
      <Paper withBorder p="md">
        {accountLoading ? (
          <Group>
            <Skeleton height={24} width={200} />
            <Skeleton height={20} width={100} />
          </Group>
        ) : account ? (
          <Group justify="space-between">
            <div>
              <Group gap="xs">
                <Title order={4}>{account.name}</Title>
                <Badge color={account.status === 'ENABLED' ? 'green' : 'red'} variant="light">
                  {account.status === 'ENABLED' ? 'Aktiv' : account.status}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed">
                Kunden-ID: {account.customerId} | Währung: {account.currency} | Zeitzone: {account.timezone}
              </Text>
            </div>
            {account.optimizationScore !== null && (
              <div style={{ textAlign: 'right' }}>
                <Text size="sm" c="dimmed">
                  Optimierungsfaktor
                </Text>
                <Group gap="xs">
                  <Text size="xl" fw={700}>
                    {Math.round(account.optimizationScore * 100)}%
                  </Text>
                  <Progress
                    value={account.optimizationScore * 100}
                    color={account.optimizationScore >= 0.8 ? 'green' : account.optimizationScore >= 0.5 ? 'yellow' : 'red'}
                    size="lg"
                    w={100}
                  />
                </Group>
              </div>
            )}
          </Group>
        ) : (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Kontodaten konnten nicht geladen werden.
          </Alert>
        )}
      </Paper>

      {/* Key Metrics */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
        <MetricCard
          title="Ausgaben"
          value={performance ? formatCurrency(performance.metrics.costMicros) : '-'}
          icon={<IconCurrencyEuro size={16} />}
          loading={performanceLoading}
          color="blue"
        />
        <MetricCard
          title="Impressionen"
          value={performance ? formatNumber(performance.metrics.impressions) : '-'}
          icon={<IconEye size={16} />}
          loading={performanceLoading}
          color="cyan"
        />
        <MetricCard
          title="Klicks"
          value={performance ? formatNumber(performance.metrics.clicks) : '-'}
          icon={<IconClick size={16} />}
          loading={performanceLoading}
          color="teal"
        />
        <MetricCard
          title="CTR"
          value={performance ? formatPercentage(performance.metrics.ctr) : '-'}
          loading={performanceLoading}
          color="green"
        />
        <MetricCard
          title="Conversions"
          value={performance ? formatNumber(performance.metrics.conversions) : '-'}
          icon={<IconTarget size={16} />}
          loading={performanceLoading}
          color="violet"
        />
        <MetricCard
          title="ROAS"
          value={performance?.metrics.roas ? `${performance.metrics.roas.toFixed(2)}x` : '-'}
          icon={<IconChartBar size={16} />}
          loading={performanceLoading}
          color="grape"
        />
      </SimpleGrid>

      {/* Performance Chart */}
      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Performance im Zeitverlauf
        </Title>
        {performanceLoading ? (
          <Skeleton height={300} />
        ) : performance?.dailyBreakdown && performance.dailyBreakdown.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performance.dailyBreakdown}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
              />
              <YAxis yAxisId="left" orientation="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip
                labelFormatter={(date) => new Date(date).toLocaleDateString('de-DE')}
                formatter={(value: number, name: string) => {
                  if (name === 'costMicros') return [formatCurrency(value), 'Ausgaben'];
                  return [formatNumber(value), name === 'clicks' ? 'Klicks' : name === 'impressions' ? 'Impressionen' : 'Conversions'];
                }}
              />
              <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="#228be6" name="clicks" />
              <Line yAxisId="left" type="monotone" dataKey="conversions" stroke="#7950f2" name="conversions" />
              <Line yAxisId="right" type="monotone" dataKey="costMicros" stroke="#40c057" name="costMicros" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Text c="dimmed" ta="center" py="xl">
            Keine Performancedaten verfügbar
          </Text>
        )}
      </Paper>

      {/* Health Indicators */}
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Konto-Statistiken
          </Title>
          {summaryLoading ? (
            <Stack gap="sm">
              {[1, 2, 3, 4].map((i) => (
                <Group key={i} justify="space-between">
                  <Skeleton height={16} width={150} />
                  <Skeleton height={16} width={50} />
                </Group>
              ))}
            </Stack>
          ) : summary ? (
            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="sm">Kampagnen gesamt</Text>
                <Text fw={500}>{summary.totals.campaigns}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Aktive Kampagnen</Text>
                <Text fw={500} c="green">
                  {summary.totals.activeCampaigns}
                </Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Anzeigengruppen</Text>
                <Text fw={500}>{summary.totals.adGroups}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Anzeigen</Text>
                <Text fw={500}>{summary.totals.ads}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Keywords</Text>
                <Text fw={500}>{summary.totals.keywords}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Asset Groups</Text>
                <Text fw={500}>{summary.totals.assetGroups}</Text>
              </Group>
            </Stack>
          ) : (
            <Text c="dimmed">Keine Daten verfügbar</Text>
          )}
        </Paper>

        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Qualitätsindikatoren
          </Title>
          {summaryLoading ? (
            <Stack gap="sm">
              {[1, 2, 3, 4].map((i) => (
                <Group key={i} justify="space-between">
                  <Skeleton height={16} width={150} />
                  <Skeleton height={16} width={50} />
                </Group>
              ))}
            </Stack>
          ) : summary ? (
            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="sm">Durchschnittlicher Quality Score</Text>
                <Badge color={summary.healthIndicators.averageQualityScore && summary.healthIndicators.averageQualityScore >= 7 ? 'green' : 'yellow'}>
                  {summary.healthIndicators.averageQualityScore?.toFixed(1) ?? '-'}
                </Badge>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Abgelehnte Anzeigen</Text>
                <Badge color={summary.healthIndicators.disapprovedAdsCount > 0 ? 'red' : 'green'}>
                  {summary.healthIndicators.disapprovedAdsCount}
                </Badge>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Eingeschränkte Anzeigen</Text>
                <Badge color={summary.healthIndicators.limitedAdsCount > 0 ? 'yellow' : 'green'}>
                  {summary.healthIndicators.limitedAdsCount}
                </Badge>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Keywords mit niedrigem QS</Text>
                <Badge color={summary.healthIndicators.lowQualityKeywordsCount > 0 ? 'yellow' : 'green'}>
                  {summary.healthIndicators.lowQualityKeywordsCount}
                </Badge>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Offene Empfehlungen</Text>
                <Badge color="blue">{summary.healthIndicators.recommendationsCount}</Badge>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Budget-limitierte Kampagnen</Text>
                <Badge color={summary.healthIndicators.budgetLimitedCampaignsCount > 0 ? 'orange' : 'green'}>
                  {summary.healthIndicators.budgetLimitedCampaignsCount}
                </Badge>
              </Group>
            </Stack>
          ) : (
            <Text c="dimmed">Keine Daten verfügbar</Text>
          )}
        </Paper>
      </SimpleGrid>
    </Stack>
  );
}
